/**
 * Strategy & planning domain registration bootstrap.
 *
 * Registers the Gantt and Scorecard domain profiles, the planning
 * relationship pack (into the connector registry), the 17 enriched
 * table semantic schemas, and the 4 reference shape manifests.
 *
 * Epic SP-A / SP-B expand `PlanningShapeManifests` with real
 * domain-specific renderers (task bars, milestone diamonds, scorecard
 * option cards, criterion headers) and add manifests for additional
 * constructs (sections, total strips, radar facets).
 *
 * Call `registerPlanningDomain()` once during app startup (same pattern
 * as `registerNetworkDomain` and `registerOrgChartDomain`).
 *
 * See plans/strategy-planning/foundation-scaffold.md § Layer 6.
 */

import { registerRelationshipPack, registerShape } from '../../../lib/diagram/registries';
import type { DiagramType } from '../../../lib/diagram/registries';
import { registerDomainProfile } from '../../../core/data-profiles';
import { registerShapeCategory } from '../../../lib/diagram/shapeCategoryRegistry';
import { Calendar, Diamond, Layers, Trophy } from 'lucide-react';

import { GanttProfile } from '../profiles/GanttProfile';
import { ScorecardProfile } from '../profiles/ScorecardProfile';
import { PlanningRelationshipPack } from '../connectors/relationships.pack';
import { PlanningTableSemanticSchemas } from '../schemas';
import { registerTableSemanticSchema } from '../schemas/registry';

import { PlanningShapeManifests } from './index';

let registered = false;

export function registerPlanningDomain(): void {
  if (registered) return;
  registered = true;

  // 1. Domain profiles
  registerDomainProfile(GanttProfile);
  registerDomainProfile(ScorecardProfile);

  // 2. Relationship pack — 5 categories, 14 subtypes covering gantt
  //    dependencies, containment, scorecard evaluation, fishbone
  //    causation (Epic SP-C surfaces), and cross-framework links
  //    (Epic SP-D surfaces).
  registerRelationshipPack(PlanningRelationshipPack);

  // 3. Table semantic schemas — 17 enriched schemas share the visual
  //    table renderer's `id` field. Epic SP-B wires the table renderer
  //    to read the typed fields from this semantic registry.
  for (const schema of PlanningTableSemanticSchemas) {
    registerTableSemanticSchema(schema);
  }

  // 4. Reference shape manifests — 4 scaffold shapes. Epic SP-A / SP-B
  //    replace the placeholder SVG renderers and add the remaining
  //    domain-specific shapes (sections, totals, radar facets, etc.).
  //    All shapes register under a single DiagramType slot for scaffold
  //    simplicity; Epic B may split by domain when it ships the real
  //    renderers.
  const shapeDiagramType = 'planning' as DiagramType;
  for (const manifest of PlanningShapeManifests) {
    registerShape(shapeDiagramType, manifest);
  }

  // 5. Shape-picker categories — self-register two tabs (Gantt + Scorecard)
  //    via the new plugin point so users can drag tasks/milestones/options
  //    onto a planning canvas. See plans/strategy-planning/playbook.md § X
  //    (App affordance wiring).
  registerShapeCategory({
    id: 'gantt',
    name: 'Gantt',
    applicableDiagramTypes: ['planning'],
    order: 50,
    shapes: [
      { id: 'gantt_section', label: 'Section', icon: Layers },
      { id: 'gantt_task', label: 'Task', icon: Calendar },
      { id: 'gantt_milestone', label: 'Milestone', icon: Diamond },
    ],
  });

  registerShapeCategory({
    id: 'scorecard',
    name: 'Scorecard',
    applicableDiagramTypes: ['planning'],
    order: 51,
    shapes: [
      { id: 'scorecard_criterion', label: 'Criterion', icon: Trophy },
      { id: 'scorecard_option', label: 'Option', icon: Trophy },
    ],
  });
}
